import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import "./Header.css";
import Button from "../MainButton/MainButton.js";
import logoImg from "../../assets/logo-gb.png";
import arrowImg from "../../assets/arrow.png";
import arrowRight from "../../assets/arrow_right.png";

export default function NavigationHeader() {
  const { isAuthenticated, logout, roleId } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [exploreOpen, setExploreOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  // Zamykaj menu po zmianie trasy
  useEffect(() => {
    if (open) setOpen(false);
  }, [location.pathname]); // [13][16]

  // Blokuj scroll tła w trakcie otwartego menu + focus trap
  useEffect(() => {
    if (open) {
      // body scroll lock (z zachowaniem pozycji)
      const y = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${y}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.classList.add("scroll-locked");
      // przenieś fokus do panelu
      const firstFocusable = panelRef.current?.querySelector("a, button");
      firstFocusable?.focus();
      return () => {
        // odblokuj i przywróć pozycję
        const scrollY = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.classList.remove("scroll-locked");
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
        // zwróć fokus na przycisk
        btnRef.current?.focus();
      };
    }
  }, [open]); // [6][12]

  // Esc zamyka panel
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]); // [3]

  return (
    <nav className="navbar" aria-label="Primary">
      {/* Burger */}
      <button
        ref={btnRef}
        className="hamburger"
        aria-label="Open menu"
        aria-controls="mobile-sidenav"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
      </button>

      {/* Logo + brand */}
      <div className="logo">
        <a href="/" aria-label="GlobTel eSIM home">
          <img src={logoImg} alt="" className="logo-img" />
          GlobTel eSIM
        </a>
      </div>

      {/* Desktop nav */}
      <nav className="nav-links" aria-label="Primary links">
        <div className="nav-group">
          <button
            className="nav-group-btn"
            aria-haspopup="menu"
            aria-expanded="false"
          >
            Explore
            <img src={arrowImg} alt="" className="arrow-icon" />
          </button>
          <div className="nav-dropdown" role="menu">
            <NavLink to="/" className="nav-item">
              Who We Serve
            </NavLink>
            <NavLink to="/features" className="nav-item">
              Features
            </NavLink>
            <NavLink to="/about-us" className="nav-item">
              About Us
            </NavLink>
          </div>
        </div>
        <NavLink to="/pricing" className="nav-item">
          Pricing
        </NavLink>
        {isAuthenticated && (
          <NavLink to="/profile" className="nav-item">
            Profile
          </NavLink>
        )}
        {isAuthenticated && (
          <NavLink to="/offer" className="nav-item">
            Offer
          </NavLink>
        )}
        {isAuthenticated && (
          <NavLink to="/partner-offers" className="nav-item">
            Partner Offers
          </NavLink>
        )}
        {isAuthenticated && (
          <NavLink to="/doc" className="nav-item">
            API
          </NavLink>
        )}

        {isAuthenticated && roleId === 3 && (
          <NavLink to="/esim-stats" className="nav-item">
            eSIM Stats
          </NavLink>
        )}

        {isAuthenticated && roleId === 2 && (
          <NavLink to="/my-esims" className="nav-item">
            My eSIMs
          </NavLink>
        )}

        <NavLink to="/contact" className="nav-item">
          Contact Us
        </NavLink>

        {!isAuthenticated ? (
          <NavLink to="/sign-in" className="btn-login">
            Sign In
          </NavLink>
        ) : (
          <button
            type="button"
            className="btn-login"
            onClick={() => {
              logout();
              navigate("/");
            }}
          >
            Sign Out
          </button>
        )}
        {!isAuthenticated && (
          <Button onClick={() => navigate("/become-a-partner")}>
            Become a Partner
          </Button>
        )}
      </nav>

      {/* Overlay */}
      {open && (
        <div
          className="overlay"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        ></div>
      )}

      {/* Sidenav (mobile) */}
      <aside
        id="mobile-sidenav"
        ref={panelRef}
        className={`sidenav ${open ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        tabIndex={-1}
      >
        <button
          className="nav-accord-btn"
          onClick={() => setExploreOpen((v) => !v)}
          aria-expanded={exploreOpen}
        >
          Explore
          <img
            src={arrowRight}
            alt=""
            className={`arrow-icon ${exploreOpen ? "open" : ""}`}
          />
        </button>
        {exploreOpen && (
          <div className="nav-accord-panel">
            <NavLink to="/" className="nav-item" onClick={() => setOpen(false)}>
              Who We Serve
            </NavLink>
            <NavLink
              to="/features"
              className="nav-item"
              onClick={() => setOpen(false)}
            >
              Features
            </NavLink>
            <NavLink
              to="/about-us"
              className="nav-item"
              onClick={() => setOpen(false)}
            >
              About Us
            </NavLink>
          </div>
        )}

        {isAuthenticated && (
          <NavLink
            to="/profile"
            className="nav-item"
            onClick={() => setOpen(false)}
          >
            Profile
          </NavLink>
        )}

        {isAuthenticated && (
          <NavLink
            to="/partner-offers"
            className="nav-item"
            onClick={() => setOpen(false)}
          >
            Partner Offers
          </NavLink>
        )}

        {isAuthenticated && (
          <NavLink to="/doc" className="nav-item">
            API
          </NavLink>
        )}

        <NavLink
          to="/pricing"
          className="nav-item"
          onClick={() => setOpen(false)}
        >
          Pricing
        </NavLink>

        {isAuthenticated && (
          <NavLink to="/offer" className="nav-item">
            Offer
          </NavLink>
        )}

        {isAuthenticated && roleId === 3 && (
          <NavLink
            to="/esim-stats"
            className="nav-item"
            onClick={() => setOpen(false)}
          >
            eSIM Stats
          </NavLink>
        )}

        {isAuthenticated && roleId === 3 && (
          <NavLink
            to="/esim-stats"
            className="nav-item"
            onClick={() => setOpen(false)}
          >
            eSIM Stats
          </NavLink>
        )}
        {isAuthenticated && roleId === 2 && (
          <NavLink
            to="/my-esims"
            className="nav-item"
            onClick={() => setOpen(false)}
          >
            My eSIMs
          </NavLink>
        )}

        <NavLink
          to="/contact"
          className="nav-item"
          onClick={() => setOpen(false)}
        >
          Contact Us
        </NavLink>
        {!isAuthenticated ? (
          <NavLink
            to="/sign-in"
            className="btn-login"
            onClick={() => setOpen(false)}
          >
            Sign In
          </NavLink>
        ) : (
          <button
            type="button"
            className="btn-login"
            onClick={() => {
              logout();
              setOpen(false);
              navigate("/");
            }}
          >
            Sign Out
          </button>
        )}

        {!isAuthenticated && (
          <Button
            onClick={() => {
              setOpen(false);
              navigate("/become-a-partner");
            }}
          >
            Become a Partner
          </Button>
        )}
      </aside>
    </nav>
  );
}
